import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getIcon, term } from "@/lib/games";
import QRBlock from "@/components/QRBlock";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Search, Zap, Play, Pause, SkipForward, Eye, Timer, Square, RotateCcw, Trophy, QrCode, Gift, History, Gamepad2, Users, Coins } from "lucide-react";
import { toast } from "sonner";

const SELFPLAY = ["memory_challenge", "match_master"];
const HOSTED = ["quiz", "rapid_fire", "mystery_vault", "who_am_i", "pixel_quest", "target_master"];

export default function GameMaster() {
  const { slug } = useParams();
  const { user, loading: authLoading, login } = useAuth();
  const [state, setState] = useState("loading"); // loading, needauth, ready
  const [event, setEvent] = useState(null);
  const [gm, setGm] = useState(null);
  const [zones, setZones] = useState([]);
  const [activeZone, setActiveZone] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [victory, setVictory] = useState(null);
  const [ttl, setTtl] = useState(0);

  const load = useCallback(async () => {
    try {
      const ev = await api.get(`/gm/events/${slug}`);
      setEvent(ev.data.event); setGm(ev.data.gm);
      const z = await api.get(`/gm/events/${slug}/zones`);
      setZones(z.data);
      setState("ready");
    } catch (e) {
      if (e.response?.status === 401) setState("needauth");
      else { toast.error(e.response?.data?.detail || "Access denied"); setState("needauth"); }
    }
  }, [slug]);

  useEffect(() => { if (!authLoading) load(); }, [authLoading, load]);
  useEffect(() => {
    if (state !== "ready") return;
    const iv = setInterval(() => api.get(`/gm/events/${slug}/zones`).then((r) => setZones(r.data)).catch(() => {}), 4000);
    return () => clearInterval(iv);
  }, [state, slug]);

  useEffect(() => {
    setActiveZone((prev) => (prev ? (zones.find((z) => z.zone_id === prev.zone_id) || prev) : prev));
  }, [zones]);

  useEffect(() => {
    if (ttl <= 0) return;
    const iv = setInterval(() => setTtl((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(iv);
  }, [ttl]);

  if (state === "loading" || authLoading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (state === "needauth") return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="brut p-8 text-center max-w-sm">
        <Gamepad2 className="w-12 h-12 mx-auto text-primary mb-4" />
        <h1 className="font-head font-black text-2xl mb-2">Game Master Console</h1>
        <p className="text-muted-foreground mb-6">Log in with the email your organizer assigned as a Game Master.</p>
        <Button className="rounded-full font-bold w-full" onClick={login} data-testid="gm-login">Log in with Google</Button>
      </div>
    </div>
  );

  const doSearch = async () => {
    const r = await api.get(`/gm/events/${slug}/players/search`, { params: { q: query } });
    setResults(r.data);
  };
  const award = async (xp) => {
    const r = await api.post(`/gm/events/${slug}/players/${selected.player_id}/award`, { xp, zone_id: activeZone?.zone_id });
    toast.success(`+${xp} ${term(event, "xp", "Cash")} to ${selected.name}!`);
    setSelected({ ...selected, total_xp: r.data.total_xp });
    doSearch();
  };
  const genVictory = async () => {
    if (!activeZone) { toast.error("Pick a zone first"); return; }
    const xp = 100;
    const r = await api.post(`/gm/events/${slug}/victory-code`, { xp, zone_id: activeZone.zone_id, ttl: 120 });
    setVictory(r.data); setTtl(r.data.ttl);
  };
  const control = async (action, payload = {}) => {
    if (!activeZone) return;
    const r = await api.post(`/gm/events/${slug}/zones/${activeZone.zone_id}/control`, { action, payload });
    setActiveZone({ ...activeZone, live: r.data.live });
    toast.success(`Screen: ${action}`);
  };

  const setMode = async (mode) => {
    if (!activeZone) return;
    try {
      await api.post(`/gm/events/${slug}/zones/${activeZone.zone_id}/mode`, { control_mode: mode });
      setActiveZone({ ...activeZone, control_mode: mode, live: { state: "idle", screen: "welcome", current_question_index: 0 } });
      toast.success(mode === "hosted" ? "Hosted mode ON — you control this booth live" : "Auto mode — participants play on their own");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not switch mode"); }
  };
  const orderedZones = [...zones].sort((a, b) => (SELFPLAY.includes(a.game_type) ? 1 : 0) - (SELFPLAY.includes(b.game_type) ? 1 : 0));

  const victoryUrl = `${window.location.origin}/e/${slug}`;

  return (
    <div className="min-h-screen">
      <header className="glass border-b border-border sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 font-head font-black"><Gamepad2 className="w-5 h-5 text-primary" />{event.name} · {term(event, "gamemaster", "Game Master")}</div>
          <span className="text-sm text-muted-foreground">{gm?.name}</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 grid lg:grid-cols-2 gap-6">
        {/* zones */}
        <div>
          <h2 className="font-head font-bold text-lg mb-3">My {term(event, "zone", "Challenge Zones")}</h2>
          <div className="space-y-3">
            {orderedZones.map((z) => { const I = getIcon(z.icon); const isActive = activeZone?.zone_id === z.zone_id; return (
              <div key={z.zone_id} className={`brut p-4 cursor-pointer lift ${isActive ? "border-primary bg-primary/5" : ""}`} onClick={() => setActiveZone(z)} data-testid={`gm-zone-${z.zone_id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center"><I className="w-5 h-5 text-primary" /></div>
                  <div className="flex-1"><p className="font-bold">{z.name}</p><p className="text-xs text-muted-foreground">{z.location || z.game_name} · {SELFPLAY.includes(z.game_type) ? "self-play only" : HOSTED.includes(z.game_type) ? (z.control_mode === "hosted" ? "🔴 hosted" : "🟢 auto") : "counter"}</p></div>
                  <div className="text-right text-xs font-mono-num text-muted-foreground"><p>{z.players_served} served</p><p className="text-gold">{z.xp_awarded} Cash</p></div>
                </div>
              </div>
            ); })}
          </div>

          {activeZone && (
            <div className="brut p-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-head font-bold">Live Control · {activeZone.name}</h3>
                {HOSTED.includes(activeZone.game_type) && (
                  <div className="flex rounded-full border-2 border-neutral-300 overflow-hidden text-xs font-bold">
                    <button onClick={() => setMode("hosted")} className={`px-3 py-1.5 ${activeZone.control_mode === "hosted" ? "bg-primary text-white" : ""}`} data-testid="mode-hosted">Hosted</button>
                    <button onClick={() => setMode("auto")} className={`px-3 py-1.5 ${activeZone.control_mode !== "hosted" ? "bg-primary text-white" : ""}`} data-testid="mode-auto">Auto</button>
                  </div>
                )}
              </div>

              {SELFPLAY.includes(activeZone.game_type) ? (
                <div className="text-sm text-muted-foreground bg-secondary rounded-xl p-3" data-testid="selfplay-note">⚠️ This game is self-play only and cannot be controlled by a moderator. Participants play it themselves — you can still award Cash from the panel on the right.</div>
              ) : (
                <>
                  {HOSTED.includes(activeZone.game_type) && activeZone.control_mode !== "hosted" && (
                    <div className="text-sm text-muted-foreground bg-secondary rounded-xl p-3 mb-3" data-testid="auto-note">🟢 Auto mode — participants are playing on their own. Controls are frozen. Switch to <b>Hosted</b> to run it live.</div>
                  )}
                  <div className={`grid grid-cols-3 gap-2 ${HOSTED.includes(activeZone.game_type) && activeZone.control_mode !== "hosted" ? "opacity-40 pointer-events-none" : ""}`} data-testid="gm-controls">
                    {[["start", Play, "Start"], ["pause", Pause, "Pause"], ["resume", Play, "Resume"], ["next", SkipForward, "Next Q"], ["show_answer", Eye, "Answer"], ["start_timer", Timer, "Timer"], ["winner", Trophy, "Winner"], ["reset", RotateCcw, "Reset"], ["stop", Square, "Stop"]].map(([a, I, l]) => (
                      <Button key={a} variant="secondary" className="rounded-xl h-16 flex-col gap-1 text-xs" onClick={() => control(a, a === "start_timer" ? { seconds: 20 } : {})} data-testid={`ctrl-${a}`}>
                        <I className="w-5 h-5" />{l}
                      </Button>
                    ))}
                  </div>
                  {activeZone.game_type === "fortune_spin" && (
                    <Button className="w-full rounded-full mt-3 font-bold bg-gold text-black" onClick={() => control("spin")} data-testid="ctrl-spin"><Zap className="w-4 h-4 mr-1" />Spin the Wheel</Button>
                  )}
                  <p className="text-xs text-muted-foreground mt-3">Big screen state: <span className="font-mono-num text-foreground">{activeZone.live?.screen || "welcome"} / {activeZone.live?.state || "idle"}</span></p>
                  <Button variant="outline" className="w-full rounded-full mt-2" onClick={() => window.open(`/display/${slug}/${activeZone.zone_id}`, "_blank")}><Eye className="w-4 h-4 mr-1" />Open Big Screen</Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* player ops */}
        <div>
          <h2 className="font-head font-bold text-lg mb-3 flex items-center gap-2"><Users className="w-4 h-4" />Find a {term(event, "player", "Player")}</h2>
          <div className="flex gap-2 mb-3">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder="Name, email, phone, ID…" data-testid="gm-search-input" />
            <Button className="rounded-full" onClick={doSearch} data-testid="gm-search-btn"><Search className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto no-scrollbar mb-4">
            {results.map((p) => (
              <button key={p.player_id} onClick={() => setSelected(p)} className={`w-full brut p-3 text-left flex items-center justify-between ${selected?.player_id === p.player_id ? "border-primary" : ""}`} data-testid={`gm-player-${p.player_id}`}>
                <div><p className="font-semibold">{p.name}</p><p className="text-xs text-muted-foreground">{p.contact} · {p.challenges} done</p></div>
                <span className="font-mono-num text-gold font-bold">{p.total_xp}</span>
              </button>
            ))}
          </div>

          {selected && (
            <div className="brut-primary bg-card p-4 rounded-2xl">
              <div className="flex items-center justify-between mb-3">
                <div><p className="font-head font-black text-xl">{selected.name}</p><p className="text-sm text-muted-foreground flex items-center gap-1">Current: <span className="font-mono-num text-cash flex items-center gap-1"><Coins className="w-4 h-4" />{selected.total_xp} Cash</span></p></div>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Award Cash {activeZone ? `(via ${activeZone.name})` : "(select a zone to tag it)"}</p>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {[10, 25, 50, 100].map((x) => (
                  <Button key={x} className="rounded-xl h-14 font-mono-num font-bold text-base brut-hard" onClick={() => award(x)} data-testid={`award-${x}`}>+{x}</Button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" className="flex-1 rounded-full" onClick={genVictory} data-testid="gm-gen-victory"><QrCode className="w-4 h-4 mr-1" />Victory QR</Button>
                <RewardBtn slug={slug} player={selected} />
                <HistoryBtn slug={slug} player={selected} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* victory QR dialog */}
      <Dialog open={!!victory} onOpenChange={(o) => !o && setVictory(null)}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader><DialogTitle className="font-head font-black text-2xl">🎉 Challenge Complete</DialogTitle></DialogHeader>
          {victory && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center justify-center gap-2 font-mono-num font-extrabold text-4xl text-cash"><Coins className="w-8 h-8" />+{victory.xp} Cash</div>
              <div className="bg-white p-3 rounded-2xl"><QRBlockLite value={`${victoryUrl}?vc=${victory.token}`} /></div>
              <p className="text-sm text-muted-foreground">Or enter code: <span className="font-mono-num text-foreground text-lg tracking-widest">{victory.token}</span></p>
              <p className={`text-sm font-mono-num ${ttl < 20 ? "text-primary" : "text-muted-foreground"}`}>Expires in {ttl}s · single use</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QRBlockLite({ value }) {
  const { QRCodeCanvas } = require("qrcode.react");
  return <QRCodeCanvas value={value} size={180} level="M" />;
}

function RewardBtn({ slug, player }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const openIt = async () => { const r = await api.get(`/gm/events/${slug}/players/${player.player_id}/rewards`); setData(r.data); setOpen(true); };
  const collect = async (rid) => {
    try { await api.post(`/gm/events/${slug}/players/${player.player_id}/rewards/${rid}/collect`); toast.success("Reward marked collected"); openIt(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (<>
    <Button variant="secondary" size="icon" className="rounded-full" onClick={openIt} data-testid="gm-rewards"><Gift className="w-4 h-4" /></Button>
    <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-sm">
      <DialogHeader><DialogTitle className="font-head">Rewards · {player.name}</DialogTitle></DialogHeader>
      <div className="space-y-2">
        {data?.rewards.map((r) => (
          <div key={r.reward_id} className="brut p-3 flex items-center justify-between">
            <div><p className="font-semibold">{r.name}</p><p className="text-xs text-muted-foreground font-mono-num">{r.min_xp}+ Cash</p></div>
            {r.collected ? <span className="text-xs text-neon-green font-bold">Collected</span> : r.eligible ? <Button size="sm" className="rounded-full" onClick={() => collect(r.reward_id)} data-testid={`collect-${r.reward_id}`}>Mark collected</Button> : <span className="text-xs text-muted-foreground">Not eligible</span>}
          </div>
        ))}
        {data?.rewards.length === 0 && <p className="text-sm text-muted-foreground">No rewards configured.</p>}
      </div>
    </DialogContent></Dialog>
  </>);
}

function HistoryBtn({ slug, player }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const openIt = async () => { const r = await api.get(`/gm/events/${slug}/players/${player.player_id}/history`); setRows(r.data); setOpen(true); };
  return (<>
    <Button variant="secondary" size="icon" className="rounded-full" onClick={openIt} data-testid="gm-history"><History className="w-4 h-4" /></Button>
    <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-sm">
      <DialogHeader><DialogTitle className="font-head">Cash History · {player.name}</DialogTitle></DialogHeader>
      <div className="space-y-2 max-h-80 overflow-y-auto no-scrollbar">
        {rows.map((t) => (
          <div key={t.txn_id} className="brut p-3 flex items-center justify-between text-sm">
            <div><p className="font-semibold">{t.zone_name || "Bonus"}</p><p className="text-xs text-muted-foreground">{t.type} · {new Date(t.created_at).toLocaleString()}</p></div>
            <span className="font-mono-num text-neon-green font-bold">+{t.xp}</span>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No transactions yet.</p>}
      </div>
    </DialogContent></Dialog>
  </>);
}
